export type Mode =
  | "default"
  | "public"
  | "non-explicit"
  | "non-explicit-public"
  | "leaflet";

// The mode selected when the page loads. Never a public-CDN mode: the
// selection is not persisted, so every visit starts here.
export const DEFAULT_MODE: Mode = "non-explicit";

// Modes whose output leaves the members estate. Leaflet lands in the public
// marketing bucket and is served unauthenticated from the leaflet CloudFront
// distribution, with no fast un-publish; every other mode stays behind the
// signed-cookie videos.omgyes.com. Mirrors PUBLIC_MARKETING_MODES in
// omgyes-backend/video-encoding/submit.py (OMG-2352).
const PUBLIC_CDN_MODES: ReadonlySet<Mode> = new Set<Mode>(["leaflet"]);

// Same wording as submit.py's PUBLIC_DESTINATION_NOTE, so the CLI and this page
// describe the destination identically.
export const PUBLIC_DESTINATION_NOTE =
  "PUBLIC marketing CDN (served unauthenticated, no fast un-publish)";

export function requiresPublicConfirmation(mode: Mode): boolean {
  return PUBLIC_CDN_MODES.has(mode);
}

// Whether starting these entries must first show the public-CDN confirmation.
// `confirmedIds` holds entries the operator already confirmed in this mode, so
// retrying one of them does not prompt again, but any entry not yet confirmed
// (a new file, or one queued under another mode) prompts for the whole set.
export function needsPublicConfirmation(
  mode: Mode,
  entryIds: string[],
  confirmedIds: ReadonlySet<string>,
): boolean {
  if (!requiresPublicConfirmation(mode)) return false;
  return entryIds.some((id) => !confirmedIds.has(id));
}

export function publishPubliclyLabel(count: number): string {
  return `Publish ${count} file${count === 1 ? "" : "s"} publicly`;
}

export function basenameFromFilename(filename: string): string {
  return filename.replace(/\.[^.]+$/, "");
}

export interface BasenameCollision {
  basename: string;
  filenames: string[];
}

// The upload key is the filename and the output prefix is its basename, so two
// files in one batch that share a basename (promo.mp4 and promo.mov, or the
// same name picked from two folders) publish to the same videos/<basename>/
// and the later upload overwrites the earlier one.
export function basenameCollisions(filenames: string[]): BasenameCollision[] {
  const byBasename = new Map<string, string[]>();
  for (const filename of filenames) {
    const basename = basenameFromFilename(filename);
    byBasename.set(basename, [...(byBasename.get(basename) ?? []), filename]);
  }
  return Array.from(byBasename, ([basename, names]) => ({
    basename,
    filenames: names,
  })).filter((c) => c.filenames.length > 1);
}

export interface PriorPublish {
  filename: string;
  mode: Mode;
  uploadedAt: number;
}

export interface PriorPublishCollision {
  filename: string;
  basename: string;
  previous: PriorPublish;
}

// Files in this batch whose output basename matches an earlier public-CDN
// upload, so publishing them replaces a video that is already live at
// videos/<basename>/. `prior` is newest first (the recent-uploads history), so
// the first match is the version being replaced. Only as complete as that
// history: uploads from another browser or the CLI, or older than its
// retention window, are not known here.
export function priorPublishCollisions(
  filenames: string[],
  prior: PriorPublish[],
): PriorPublishCollision[] {
  const published = prior.filter((p) => requiresPublicConfirmation(p.mode));
  return filenames.flatMap((filename) => {
    const basename = basenameFromFilename(filename);
    const previous = published.find(
      (p) => basenameFromFilename(p.filename) === basename,
    );
    return previous ? [{ filename, basename, previous }] : [];
  });
}
